import {
  Button,
  Loader,
  TextButton,
} from '@maersktankersdigital/web-components'
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  IQuestionnaireField,
  IQuestionnairesResponse,
  IVesselFile,
} from '~api/vessels/read'
import { AnimatedAlert } from '~components/molecules/animated-alert/animated-alert'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import { Modal } from '~components/molecules/modal/modal'
import {
  PostFilePayload,
  usePostFiles,
} from '~hooks/queries/vessels/file/use-post-file'
import { useGetQuestionnaires } from '~hooks/queries/vessels/use-get-questionnaires'
import {
  usePatchSection,
  PatchSectionPayload,
} from '~hooks/queries/vessels/use-patch-section'
import { exhaustiveCheck } from '~pages/pages-behind-login/financials/earnings/utils/format-date-by-aggregate'
import { paramName } from '../constants'
import { useDeleteVesselFiles } from '../use-get-vessel-file-url'
import { AddComment } from './add-comment'
import { AddFiles } from './add-files'
import { DeleteConfirmation } from './delete-confirmation'
import { DownloadLink } from './download-link'
import { DragAndDropFiles } from './drag-and-drop-files'
import { InputDisplay } from './input-display'
import { NameDescriptionDetails } from './name-description-details'
import { RadioChoice } from './radio-choice'
import { UploadedFile } from './uploaded-file'

export interface Field {
  id: string
  value?: string
  notApplicable?: boolean
  comment?: string | null
}

export function OpenSectionModal({
  manualQuestions,
  uniqueSections,
  revalidate,
}: {
  manualQuestions?: IQuestionnairesResponse
  uniqueSections?: string[]
  revalidate?: () => void
}) {
  const { vesselId } = useParams()
  const {
    data,
    refetch,
    isLoading: isLoadingQuestionnaires,
  } = useGetQuestionnaires(vesselId)

  const questionnaireData = manualQuestions ? manualQuestions : data

  const { setStatus } = useAnimatedAlert()
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionParam = searchParams.get(paramName)
  const flowParam = searchParams.get('flow') || undefined
  const paramValueToUse = flowParam ? flowParam : sectionParam
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false)
  const [inputData, setInputData] = useState<Field[]>()
  const [filesToDelete, setFilesToDelete] = useState<IVesselFile[]>()
  const [filesToPost, setFilesToPost] = useState<PostFilePayload[]>()
  const { mutate: postFiles, status: postFilesStatus } = usePostFiles(vesselId)
  const { mutate: patchSection, status: patchSectionStatus } = usePatchSection(
    vesselId,
    flowParam,
  )
  const { mutate: deleteFiles, status: deleteFilesStatus } =
    useDeleteVesselFiles()

  const section = useMemo(() => {
    return questionnaireData
      ?.flatMap((item) => item.sections)
      ?.find((section) => section.id === paramValueToUse)
  }, [questionnaireData, data, manualQuestions, paramValueToUse])

  if (
    !paramValueToUse ||
    isLoadingQuestionnaires ||
    !questionnaireData ||
    !vesselId
  ) {
    return null
  }

  // if (!section) {
  //   setStatus('Section not found.', undefined, 'error')
  //   console.log({
  //     section,
  //     sectionAgain: questionnaireData,
  //   })
  //   closeModal()
  //   return null
  // }

  const isLoading =
    patchSectionStatus === 'pending' ||
    deleteFilesStatus === 'pending' ||
    postFilesStatus === 'pending'

  function closeModal() {
    const newSearchParams = new URLSearchParams(searchParams.toString())
    newSearchParams.delete(paramName)
    newSearchParams.delete('flow')
    setSearchParams(newSearchParams)
  }

  function onSuccess() {
    if (isLoading) return
    refetch()
    revalidate?.()
    setInputData(undefined)
    setFilesToPost(undefined)
    setFilesToDelete(undefined)
    setStatus(flowParam ? 'Flow completed.' : 'Section saved.', 3000, 'success')
    closeModal()
  }

  function onError() {
    setStatus('An error occurred. Please try again.', undefined, 'error')
  }

  function updateInput({ id, value, notApplicable, comment }: Field) {
    const newItem = {
      id,
      ...(value !== undefined && { value }),
      ...(notApplicable !== undefined && { notApplicable }),
      ...(comment !== undefined && { comment }),
    }

    if (!inputData || inputData.length === 0) {
      setInputData([newItem])
      return
    }

    const updatedData = inputData.map((item) =>
      item.id === id ? { ...item, ...newItem } : item,
    )

    if (!inputData.some((item) => item.id === id)) {
      updatedData.push(newItem)
    }

    setInputData(updatedData)
  }

  const save = async () => {
    if (filesToPost?.length) {
      postFiles(filesToPost, { onSuccess, onError })
    }

    if (filesToDelete?.length) {
      const fileNames = filesToDelete.map((file) => file.name)
      deleteFiles(fileNames, { onSuccess, onError })
    }

    if (inputData?.length && section) {
      if (manualQuestions) {
        const batchesMap = new Map<string, PatchSectionPayload>()

        inputData.forEach((input) => {
          const sectionId =
            section.fields.find((f) => f.id === input.id)?.sectionId || ''

          if (!batchesMap.has(sectionId)) {
            batchesMap.set(sectionId, { id: sectionId, fields: [input] })
          } else {
            batchesMap.get(sectionId)!.fields.push(input)
          }
        })

        const batches: PatchSectionPayload[] = Array.from(batchesMap.values())

        await Promise.all(
          batches.map((b) => patchSection(b, { onSuccess, onError })),
        )
      } else {
        patchSection(
          {
            id: section.id,
            fields: inputData,
          },
          { onSuccess, onError },
        )
      }
    }

    if (!filesToPost?.length && !filesToDelete?.length && !inputData?.length) {
      closeModal()
    }
  }

  function onClickHandler() {
    const commentsToDelete = inputData?.filter((i) => i.comment === null)

    if (filesToDelete?.length || commentsToDelete?.length) {
      setIsConfirmDeleteVisible(true)
      return
    }
    save()
  }

  function renderFieldByType(field: IQuestionnaireField) {
    const { id, type, downloadLink } = field

    switch (type) {
      case 'free_text':
      case 'number':
        return (
          <InputDisplay
            {...field}
            isDisabled={!section?.canEdit}
            inputData={inputData}
            onChangeHandler={(value) => {
              updateInput({ id, value })
            }}
          />
        )
      case 'single_choice':
        return (
          <RadioChoice
            {...field}
            isDisabled={!section?.canEdit}
            inputData={inputData}
            onChangeHandler={updateInput}
          />
        )
      case 'downl':
        return downloadLink ? <DownloadLink {...field} /> : null
      case 'downl_upl':
        return (
          <>
            {downloadLink && <DownloadLink {...field} />}
            <DragAndDropFiles
              isDisabled={!section?.canEdit}
              onChangeHandler={(files) => {
                setFilesToPost((prev) => [
                  ...(prev ? prev : []),
                  ...files.map((file) => ({
                    id,
                    file,
                  })),
                ])
              }}
            />
          </>
        )
    }
    exhaustiveCheck(type)
  }

  function renderFieldEnhancements(field: IQuestionnaireField) {
    const {
      id,
      type,
      files,
      allowNotApplicable,
      notApplicable,
      allowFiles,
      allowComments,
    } = field

    const filesToRender = files?.filter(
      (file) => !filesToDelete?.includes(file),
    )
    return (
      <>
        {!!filesToRender?.length && vesselId && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              py: 2,
            }}
          >
            {filesToRender?.map((file, i) => (
              <UploadedFile
                key={`${file.filename}-${i}`}
                file={file}
                sectionId={id}
                vesselId={vesselId}
                onDelete={
                  !section?.canEdit
                    ? undefined
                    : () => setFilesToDelete([...(filesToDelete || []), file])
                }
              />
            ))}
          </Box>
        )}
        {allowNotApplicable && (
          <FormControlLabel
            label="Not applicable"
            control={
              <Checkbox
                disabled={!section?.canEdit}
                onChange={(e, isChecked) =>
                  updateInput({ id, notApplicable: isChecked })
                }
                checked={
                  inputData?.some(
                    (i) => i.id === id && i.notApplicable !== undefined,
                  )
                    ? inputData?.find((i) => i.id === id)?.notApplicable
                    : notApplicable
                }
              />
            }
          />
        )}

        {section?.canEdit && allowFiles && type !== 'downl_upl' && (
          <AddFiles
            onChangeHandler={(files) => {
              setFilesToPost(
                files.map((file) => ({
                  id,
                  file,
                })),
              )
            }}
          />
        )}
        {allowComments && (
          <AddComment
            {...field}
            isDisabled={!section?.canEdit}
            inputData={inputData}
            onChangeHandler={(comment) => {
              updateInput({ id, comment: comment || null })
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Modal
        title={section?.name}
        open={!!paramValueToUse}
        onClose={closeModal}
      >
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 6, mb: 4 }}
              >
                {uniqueSections?.length === 0 && (
                  <Typography variant="paragraphBold1">
                    No data for this vessel.
                  </Typography>
                )}
                {uniqueSections?.length && manualQuestions
                  ? uniqueSections.map((sectionName) => (
                      <Box key={sectionName}>
                        <Typography variant="paragraphBold1">
                          {sectionName}
                        </Typography>
                        <Divider sx={{ mb: 4 }} />
                        {section?.fields
                          .filter((f) => f.sectionTitle === sectionName)
                          .map((field) => (
                            <Box key={field.id} sx={{ mb: 6 }}>
                              <NameDescriptionDetails {...field} />
                              {renderFieldByType(field)}
                              {renderFieldEnhancements(field)}
                            </Box>
                          ))}
                      </Box>
                    ))
                  : section?.fields.map((field) => {
                      return (
                        <Box key={field.id}>
                          {manualQuestions && field.sectionTitle && (
                            <>
                              <Typography variant="paragraphBold1">
                                {field.sectionTitle}
                              </Typography>
                              <Divider sx={{ mb: 4 }} />
                            </>
                          )}
                          <NameDescriptionDetails {...field} />
                          {renderFieldByType(field)}
                          {renderFieldEnhancements(field)}
                        </Box>
                      )
                    })}
              </Box>
              {section?.canEdit && (
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}
                >
                  <Box
                    component={TextButton}
                    onClick={closeModal}
                    sx={{ ml: 3 }}
                  >
                    Cancel
                  </Box>
                  <Button onClick={onClickHandler}>
                    {flowParam ? 'Complete' : 'Save'}
                  </Button>
                </Box>
              )}
              {isConfirmDeleteVisible && section && (
                <DeleteConfirmation
                  section={section}
                  inputData={inputData}
                  filesToDelete={filesToDelete}
                  onConfirm={() => {
                    save()
                    setIsConfirmDeleteVisible(false)
                  }}
                  onCancel={() => {
                    setIsConfirmDeleteVisible(false)
                  }}
                />
              )}
            </>
          )}
        </>
      </Modal>

      <AnimatedAlert />
    </>
  )
}
